import React from 'react';
import { projects, actorInfo } from '../mock/komalData';
import { Film, Play, ExternalLink, Theater } from 'lucide-react';
import LetterboxdIcon from './LetterboxdIcon';
import ImdbIcon from './ImdbIcon';

const WorkProjects = () => {
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('youtu.be/')[1]?.split('?')[0] || url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
  };

  // Separate screen work and theatre productions
  const screenWork = projects.filter(p => p.type !== 'Theatre Production');
  const theatreWork = projects.filter(p => p.type === 'Theatre Production');

  return (
    <section id="work" className="bg-[#0a0a0a] py-16 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-display text-5xl sm:text-6xl font-black text-white mb-4">
            MY <span className="text-[#FF6B6B]">WORK</span>
          </h2>
          <div className="w-20 h-1 bg-[#FF6B6B] mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Featured screen projects and notable theatre productions
          </p>
          {(actorInfo.letterboxd || actorInfo.imdb) && (
            <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
              {actorInfo.imdb && (
                <a
                  href={actorInfo.imdb}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="work-imdb-link"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-gray-800 rounded-full text-gray-300 hover:text-white hover:border-[#F5C518]/50 transition-colors group"
                >
                  <ImdbIcon size={18} />
                  <span className="font-medium text-sm">View IMDb</span>
                  <ExternalLink size={13} className="group-hover:translate-x-1 transition-transform" />
                </a>
              )}
              {actorInfo.letterboxd && (
                <a
                  href={actorInfo.letterboxd}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="work-letterboxd-link"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-gray-800 rounded-full text-gray-300 hover:text-white hover:border-[#00E054]/50 transition-colors group"
                >
                  <LetterboxdIcon size={18} />
                  <span className="font-medium text-sm">Find me on Letterboxd</span>
                  <ExternalLink size={13} className="group-hover:translate-x-1 transition-transform" />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Screen Projects */}
        <div className="space-y-10 mb-12">
          {/* Mobile: Compact cards for ALL screen projects */}
          {screenWork.map((project, index) => (
              <div key={`mobile-${index}`} className="lg:hidden" data-testid={`work-project-mobile-${index}`}>
                <div className="bg-[#1a1a1a] p-4 rounded-lg border border-gray-800 hover:border-[#FF6B6B]/30 transition-colors">
                  {/* Video Thumbnail */}
                  <div className="relative group mb-3">
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-lg blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                    <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                      <iframe
                        src={getYouTubeEmbedUrl(project.videoUrl)}
                        title={project.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      ></iframe>
                    </div>
                  </div>
                  
                  {/* Project Info */}
                  <div className="space-y-2">
                    <div className="inline-block px-2 py-0.5 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-full">
                      <span className="text-[#FF6B6B] text-[10px] font-semibold uppercase tracking-wider">
                        {project.type}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white">
                      {project.title}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="text-[#4ECDC4] font-semibold">{project.role}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">{project.year}</span>
                    </div>

                    <p className="text-gray-300 text-sm leading-snug whitespace-pre-line">
                      {project.description}
                    </p>

                    {project.videoUrl && (
                      <a
                        href={project.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#FF6B6B] hover:text-[#FF8E53] transition-colors group mt-2"
                      >
                        <Play size={14} />
                        <span className="font-medium text-xs">{project.linkText || 'Watch Full Video'}</span>
                        <ExternalLink size={12} className="group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
          ))}
          
          {/* Desktop: Original layout for all projects */}
          {screenWork.map((project, index) => (
            <div
              key={`desktop-${index}`}
              className={`hidden lg:grid lg:grid-cols-2 gap-6 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Video Side */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-lg blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                    <iframe
                      src={getYouTubeEmbedUrl(project.videoUrl)}
                      title={project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="space-y-3">
                  <div className="inline-block px-3 py-1 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-full">
                    <span className="text-[#FF6B6B] text-xs font-semibold uppercase tracking-wider">
                      {project.type}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-white">
                    {project.title}
                  </h3>
                  
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="text-[#4ECDC4] font-semibold">{project.role}</span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-400">{project.year}</span>
                  </div>

                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                    {project.description}
                  </p>

                  {project.videoUrl && (
                    <a
                      href={project.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#FF6B6B] hover:text-[#FF8E53] transition-colors group mt-2"
                    >
                      <Play size={18} />
                      <span className="font-medium text-sm">{project.linkText || 'Watch Full Video'}</span>
                      <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Theatre Productions - Compact */}
        {theatreWork.length > 0 && (
          <div className="bg-[#1a1a1a] p-6 rounded-lg border border-gray-800">
            <div className="flex items-center gap-2 mb-4">
              <Theater className="text-[#FFD700]" size={24} />
              <h3 className="text-xl font-bold text-white">Notable Theatre Productions</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {theatreWork.map((project, index) => (
                <div key={index} className="bg-[#0a0a0a] p-4 rounded-lg border border-gray-800 hover:border-[#FFD700]/30 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-lg font-bold text-white">{project.title}</h4>
                    <span className="text-gray-500 text-sm whitespace-nowrap">{project.year}</span>
                  </div>
                  <p className="text-[#FFD700] text-sm font-medium mb-1">{project.role}</p>
                  <p className="text-gray-400 text-xs mb-2">{project.description}</p>
                  <p className="text-[#4ECDC4] text-xs">
                    <span className="text-gray-500">Directed by:</span> {project.director}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Experience Summary - Compact */}
        <div className="mt-10 bg-gradient-to-r from-[#1a1a1a] to-[#0a0a0a] p-4 lg:p-6 rounded-lg border border-gray-800">
          <div className="grid grid-cols-3 gap-3 lg:gap-6 text-center">
            <div>
              <div className="text-[#FF6B6B] text-xs lg:text-base font-semibold mb-0.5 lg:mb-1">Screen</div>
              <div className="text-gray-400 text-[10px] lg:text-sm leading-tight">Films, Web & Music Videos</div>
            </div>
            <div>
              <div className="text-[#4ECDC4] text-xs lg:text-base font-semibold mb-0.5 lg:mb-1">Theatre</div>
              <div className="text-gray-400 text-[10px] lg:text-sm leading-tight">4+ Years Performance</div>
            </div>
            <div>
              <div className="text-[#FFD700] text-xs lg:text-base font-semibold mb-0.5 lg:mb-1">Focus</div>
              <div className="text-gray-400 text-[10px] lg:text-sm leading-tight">Lead & Primary Roles in Movies and Web Series</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkProjects;
